import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Calendar, Building2, Bell, CheckCircle2, ArrowRight } from "lucide-react";

export const THEMES = {
  indigo: { badge: "bg-indigo-600 text-white", dot: "bg-indigo-600", soft: "bg-indigo-50", bar: "bg-indigo-600" },
  blue: { badge: "bg-sky-500 text-white", dot: "bg-sky-500", soft: "bg-sky-50", bar: "bg-sky-500" },
  red: { badge: "bg-rose-500 text-white", dot: "bg-rose-500", soft: "bg-rose-50", bar: "bg-rose-500" },
  orange: { badge: "bg-amber-500 text-black", dot: "bg-amber-500", soft: "bg-amber-50", bar: "bg-amber-500" },
  green: { badge: "bg-emerald-600 text-white", dot: "bg-emerald-600", soft: "bg-emerald-50", bar: "bg-emerald-600" },
  teal: { badge: "bg-teal-600 text-white", dot: "bg-teal-600", soft: "bg-teal-50", bar: "bg-teal-600" },
};

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

export function PostCard({ post, theme = "indigo", onOpen }) {
  const t = THEMES[theme] || THEMES.indigo;
  return (
    <button onClick={() => onOpen(post)} data-testid={`post-card-${post.id}`}
      className="snap-start shrink-0 w-[280px] text-left bg-white border-2 border-black rounded-xl overflow-hidden shadow-[3px_3px_0_0_#000] hover:shadow-[5px_5px_0_0_#000] hover:-translate-y-0.5 transition-all">
      <div className={`h-36 ${t.soft} overflow-hidden border-b-2 border-black`}>
        {post.cover ? <img src={post.cover} alt="" className="w-full h-full object-cover" />
          : <div className={`w-full h-full ${t.soft} grid place-items-center text-black/30 text-sm`}>Ponderr</div>}
      </div>
      <div className="p-3 space-y-1.5">
        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${t.badge}`}>{(post.category || "").replace("_", " ")}</span>
        <div className="flex items-center gap-2 text-[11px] text-black/50">
          <Calendar size={12} /> {fmtDate(post.published_at)} · <Building2 size={12} /> {post.entity_name}
        </div>
        <h4 className="font-bold leading-tight line-clamp-2">{post.title}</h4>
        <p className="text-sm text-black/60 line-clamp-3">{post.excerpt}</p>
      </div>
    </button>
  );
}

export function HorizontalPostSlider({ title, subtitle, posts = [], themeColor = "indigo", onOpen }) {
  const ref = useRef(null);
  const t = THEMES[themeColor] || THEMES.indigo;
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const check = () => {
    const el = ref.current; if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };
  useEffect(() => { check(); }, [posts]);
  const scrollBy = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * 280 * 2, behavior: "smooth" }); };
  return (
    <section className="py-6" data-testid={`slider-${themeColor}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-8 rounded ${t.bar}`} />
            <div>
              <h3 className="display text-xl md:text-2xl font-black">{title}</h3>
              {subtitle && <p className="text-sm text-black/50">{subtitle}</p>}
            </div>
          </div>
          <div className="hidden md:flex gap-2">
            <button onClick={() => scrollBy(-1)} disabled={atStart} data-testid={`slider-prev-${themeColor}`}
              className={`w-9 h-9 grid place-items-center border-2 border-black rounded-full bg-white ${atStart ? "opacity-30" : "hover:bg-black hover:text-white"}`}><ChevronLeft size={18} /></button>
            <button onClick={() => scrollBy(1)} disabled={atEnd} data-testid={`slider-next-${themeColor}`}
              className={`w-9 h-9 grid place-items-center border-2 border-black rounded-full bg-white ${atEnd ? "opacity-30" : "hover:bg-black hover:text-white"}`}><ChevronRight size={18} /></button>
          </div>
        </div>
        {posts.length === 0 ? (
          <div className={`rounded-xl border-2 border-dashed border-black/20 ${t.soft} py-10 text-center text-black/40 text-sm`}>New updates publishing soon!</div>
        ) : (
          <div ref={ref} onScroll={check} className="flex gap-4 overflow-x-auto pb-2 no-scrollbar"
            style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
            {posts.map((p) => <PostCard key={p.id} post={p} theme={themeColor} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </section>
  );
}

export function PostReadingDrawer({ post, onClose }) {
  if (!post) return null;
  const images = ((post.media?.direct_upload_files) || []).filter((f) => f.media_type === "image").map((f) => f.url)
    .concat((post.media?.linked_media_urls) || []).concat(post.cover ? [post.cover] : []);
  const uniq = [...new Set(images)];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="post-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto border-l-2 border-black animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-3 flex items-center justify-between">
          <span className="font-bold">{post.entity_name}</span>
          <button onClick={onClose} data-testid="post-drawer-close" className="w-8 h-8 grid place-items-center border-2 border-black rounded-full hover:bg-black hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {uniq[0] && <img src={uniq[0]} alt="" className="w-full rounded-xl border-2 border-black" />}
          <div className="text-xs text-black/50">{fmtDate(post.published_at)}</div>
          <h2 className="display text-2xl font-black">{post.title}</h2>
          <p className="text-black/70">{post.excerpt}</p>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: post.content_html || "" }} />
          {uniq.length > 1 && (
            <div className="grid grid-cols-2 gap-2">{uniq.slice(1).map((u, i) => <img key={i} src={u} alt="" className="rounded-lg border-2 border-black" />)}</div>
          )}
          {(post.tags || []).length > 0 && <div className="flex flex-wrap gap-1">{post.tags.map((tg) => <span key={tg} className="text-[11px] px-2 py-0.5 bg-black/5 rounded-full">#{tg}</span>)}</div>}
        </div>
      </div>
    </div>
  );
}

const ROLE_PILLS = [
  { id: "student", label: "🎓 Student" }, { id: "educator", label: "👨‍🏫 Teacher" },
  { id: "school_leader", label: "🏫 School Owner" }, { id: "bookstore_vendor", label: "📚 Bookstore" },
  { id: "academy_creator", label: "💡 Academy" }, { id: "parent", label: "👨‍👩‍👧 Parent" },
];

export function SubscriberNewsletterSection({ entityId = null, sourcePostId = null }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ full_name: "", phone: "", email: "" });
  const [role, setRole] = useState("student");
  const [meta, setMeta] = useState({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setM = (k, v) => setMeta((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.full_name.trim() || !f.phone.trim()) return toast.error("Name and WhatsApp number required");
    setBusy(true);
    try {
      await api.post("/subscribers/register", { ...f, role, role_metadata: meta, entity_id: entityId, source_post_id: sourcePostId });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not subscribe"); }
    setBusy(false);
  };

  if (done) return (
    <div className="max-w-2xl mx-auto my-10 text-center bg-emerald-50 border-2 border-black rounded-2xl p-8 shadow-[5px_5px_0_0_#000]" data-testid="subscribe-success">
      <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
      <h3 className="display text-2xl font-black mt-2">You're in!</h3>
      <p className="text-black/60">Check your WhatsApp for the welcome kit. 🔔</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto my-12 bg-white border-2 border-black rounded-2xl p-6 shadow-[6px_6px_0_0_#000]" data-testid="subscriber-section">
      <h3 className="display text-2xl font-black">Get ecosystem updates on WhatsApp</h3>
      <p className="text-black/50 text-sm mb-4">Announcements, course launches, book deals & olympiad alerts — straight to your phone.</p>
      {step === 1 ? (
        <div className="space-y-3" data-testid="subscribe-step1">
          <input className="nb-input" placeholder="Full name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="sub-name" />
          <input className="nb-input" placeholder="WhatsApp number (+91)" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="sub-phone" />
          <input className="nb-input" placeholder="Email (optional)" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="sub-email" />
          <button className="nb-btn-primary w-full" onClick={() => { if (!f.full_name.trim() || !f.phone.trim()) return toast.error("Name & number required"); setStep(2); }} data-testid="sub-next">Continue</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="subscribe-step2">
          <div className="text-sm font-bold">I am a:</div>
          <div className="flex flex-wrap gap-2">
            {ROLE_PILLS.map((r) => (
              <button key={r.id} onClick={() => { setRole(r.id); setMeta({}); }} data-testid={`sub-role-${r.id}`}
                className={`px-3 py-1.5 rounded-full border-2 border-black text-sm font-bold ${role === r.id ? "bg-[#C4B5FD]" : "bg-white hover:bg-black/5"}`}>{r.label}</button>
            ))}
          </div>
          {(role === "student") && (
            <div className="grid grid-cols-2 gap-2">
              <input className="nb-input" placeholder="Grade" onChange={(e) => setM("grade", e.target.value)} data-testid="sub-grade" />
              <input className="nb-input" placeholder="Target stream" onChange={(e) => setM("target_stream", e.target.value)} />
            </div>
          )}
          {role === "parent" && <input className="nb-input" placeholder="Child's grade" onChange={(e) => setM("child_grade", e.target.value)} data-testid="sub-child-grade" />}
          {role === "school_leader" && (
            <div className="grid grid-cols-2 gap-2">
              <input className="nb-input" placeholder="Institution name" onChange={(e) => setM("institution_name", e.target.value)} data-testid="sub-institution" />
              <input className="nb-input" placeholder="City" onChange={(e) => setM("city", e.target.value)} />
            </div>
          )}
          {role === "bookstore_vendor" && <input className="nb-input" placeholder="Store name" onChange={(e) => setM("store_name", e.target.value)} data-testid="sub-store" />}
          {role === "academy_creator" && <input className="nb-input" placeholder="Academy name" onChange={(e) => setM("academy_name", e.target.value)} />}
          <div className="flex gap-2">
            <button className="nb-btn" onClick={() => setStep(1)}>Back</button>
            <button className="nb-btn-primary flex-1" onClick={submit} disabled={busy} data-testid="sub-submit">
              <Bell size={15} className="inline mr-1" /> {busy ? "Subscribing…" : "Subscribe for Updates on WhatsApp"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Public landing carousel — single-row horizontal scroll with nav arrows.
// "Read More" links navigate to the dedicated post page at /posts/{id}.
// -------------------------------------------------------------------------
function CarouselCard({ post, theme = "indigo" }) {
  const t = THEMES[theme] || THEMES.indigo;
  return (
    <div className="snap-start shrink-0 w-[270px] sm:w-[300px] bg-white border-2 border-black rounded-xl overflow-hidden shadow-[3px_3px_0_0_#000] flex flex-col"
      data-testid={`carousel-card-${post.id}`}>
      <div className={`h-36 ${t.soft} overflow-hidden border-b-2 border-black`}>
        {post.cover ? <img src={post.cover} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-black/30 text-sm">Ponderr</div>}
      </div>
      <div className="p-3 space-y-1.5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[11px] text-black/50 flex-wrap">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(post.published_at)}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white ${t.dot}`}><Building2 size={11} /> {post.entity_name}</span>
        </div>
        <h4 className="font-bold leading-tight line-clamp-2">{post.title}</h4>
        <p className="text-sm text-black/60 line-clamp-3 flex-1">{post.excerpt}</p>
        <Link to={`/posts/${post.id}`} data-testid={`carousel-readmore-${post.id}`}
          className="inline-flex items-center gap-1 text-sm font-bold mt-1 self-start border-b-2 border-black hover:gap-2 transition-all">
          Read More <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

export function HorizontalPostCarousel({ title, subtitle, posts = [], themeColor = "indigo", testid = "post-carousel" }) {
  const ref = useRef(null);
  const t = THEMES[themeColor] || THEMES.indigo;
  const scroll = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * 300, behavior: "smooth" }); };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-3 h-8 rounded ${t.bar}`} />
        <div className="flex-1">
          <h2 className="display text-2xl sm:text-3xl font-black">{title}</h2>
          {subtitle && <p className="text-sm text-black/50">{subtitle}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => scroll(-1)} data-testid={`${testid}-prev`}
            className="w-9 h-9 grid place-items-center border-2 border-black rounded-full bg-white hover:bg-black hover:text-white transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => scroll(1)} data-testid={`${testid}-next`}
            className="w-9 h-9 grid place-items-center border-2 border-black rounded-full bg-white hover:bg-black hover:text-white transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
      {posts.length === 0 ? (
        <div className={`rounded-xl border-2 border-dashed border-black/20 ${t.soft} py-10 text-center text-black/40 text-sm`} data-testid={`${testid}-empty`}>
          New updates publishing soon — check back shortly!
        </div>
      ) : (
        <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 no-scrollbar"
          style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
          {posts.map((p) => <CarouselCard key={p.id} post={p} theme={themeColor} />)}
        </div>
      )}
    </section>
  );
}

const SUBSCRIBE_ROLES = [
  { id: "student", label: "Student" }, { id: "educator", label: "Teacher" },
  { id: "school_leader", label: "School Owner" }, { id: "bookstore_vendor", label: "Store" },
  { id: "academy_creator", label: "Academy" }, { id: "parent", label: "Parent" },
];

// Compact single-card subscribe form (upsert on phone) for the landing page.
export function SubscribeCard({ entityId = null, className = "" }) {
  const [f, setF] = useState({ full_name: "", phone: "", email: "" });
  const [role, setRole] = useState("student");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim() || !f.phone.trim()) return toast.error("Name and WhatsApp number are required");
    setBusy(true);
    try {
      await api.post("/subscribers/register", { ...f, role, entity_id: entityId });
      setDone(true);
      toast.success("Subscribed! Check your WhatsApp for a welcome note.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not subscribe"); }
    setBusy(false);
  };

  if (done) return (
    <div className={`nb-card p-8 h-full flex flex-col items-center justify-center text-center bg-[#86EFAC] ${className}`} data-testid="subscribe-card-success">
      <CheckCircle2 size={44} className="text-emerald-800" />
      <h3 className="display text-2xl font-black mt-2">You're subscribed!</h3>
      <p className="text-sm mt-1">We'll keep you posted on news & updates.</p>
    </div>
  );

  return (
    <div className={`nb-card p-6 h-full flex flex-col bg-[#C4B5FD] ${className}`} data-testid="subscribe-card">
      <h2 className="display text-2xl sm:text-3xl font-bold mb-1">Subscribe to News &amp; Updates</h2>
      <p className="text-sm mb-4 opacity-80">Announcements, launches & deals — straight to your WhatsApp.</p>
      <form onSubmit={submit} className="flex flex-col gap-3 flex-1">
        <input className="nb-input" placeholder="Full Name" value={f.full_name}
          onChange={(e) => set("full_name", e.target.value)} data-testid="subscribe-name" required />
        <input className="nb-input" placeholder="WhatsApp Number (+91)" value={f.phone}
          onChange={(e) => set("phone", e.target.value)} data-testid="subscribe-phone" required />
        <input className="nb-input" type="email" placeholder="Email" value={f.email}
          onChange={(e) => set("email", e.target.value)} data-testid="subscribe-email" />
        <select className="nb-select" value={role} onChange={(e) => setRole(e.target.value)} data-testid="subscribe-role">
          {SUBSCRIBE_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <button className="nb-btn justify-center mt-auto" disabled={busy} data-testid="subscribe-submit">
          <Bell size={15} /> {busy ? "Subscribing…" : "Subscribe"}
        </button>
      </form>
    </div>
  );
}

